import { Button, Text, TextField } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import { useCallback, useState } from 'react';
import { Form } from 'react-aria-components';

import {
	consumeLandingInstagram,
	consumeLandingWebsite,
} from '@/screens/login/sections/landingStorage';

import { TypewriterOnce } from '../components/TypewriterOnce';

type InputScreenProps = {
	initialWebsiteUrl: string;
	initialInstagramHandle: string;
	onSubmit: (data: { websiteUrl: string; instagramHandle: string }) => void;
};

const labelStyle: React.CSSProperties = {
	textTransform: 'uppercase',
	letterSpacing: '0.12em',
	fontSize: 12,
	fontWeight: 600,
	color: 'var(--text-muted)',
};

export const InputScreen = ({
	initialWebsiteUrl,
	initialInstagramHandle,
	onSubmit,
}: InputScreenProps) => {
	const [websiteUrl, setWebsiteUrl] = useState(() =>
		consumeLandingWebsite(initialWebsiteUrl),
	);
	const [instagramHandle, setInstagramHandle] = useState(() =>
		consumeLandingInstagram(initialInstagramHandle),
	);
	const [typingDone, setTypingDone] = useState(false);
	const handleTypingDone = useCallback(() => setTypingDone(true), []);

	const canSubmit =
		websiteUrl.trim().length > 0 && instagramHandle.trim().length > 0;

	const handleSubmit = useCallback(() => {
		if (!canSubmit) return;
		onSubmit({ websiteUrl, instagramHandle });
	}, [canSubmit, websiteUrl, instagramHandle, onSubmit]);

	return (
		<div className="flex h-full w-full items-center justify-center">
			<div className="w-full max-w-[760px] px-8">
				<h2
					className="mb-10"
					style={{
						fontSize: 'clamp(26px, 4vw, 40px)',
						fontWeight: 700,
						letterSpacing: '-0.02em',
						lineHeight: 1.15,
						color: 'var(--text-primary)',
					}}
				>
					<TypewriterOnce
						text="Let's start with the basics. Your site and Instagram — I'll get up to speed on your business while we keep talking."
						onDone={handleTypingDone}
					/>
				</h2>

				<div
					style={{
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
						pointerEvents: typingDone ? 'auto' : 'none',
					}}
				>
					<Form
						className="flex flex-col gap-6"
						onSubmit={(e) => {
							e.preventDefault();
							handleSubmit();
						}}
					>
						<TextField
							label={<span style={labelStyle}>Your website</span>}
							placeholder="yourwebsite.com"
							value={websiteUrl}
							onChange={setWebsiteUrl}
							type="text"
							size="L"
						/>

						<TextField
							label={<span style={labelStyle}>Your Instagram handle</span>}
							placeholder="@yourbusiness"
							value={instagramHandle}
							onChange={setInstagramHandle}
							size="L"
						/>

						<div className="mt-4">
							<Button
								variant="primary"
								staticColor="black"
								fillStyle="fill"
								size="L"
								type="submit"
								isDisabled={!canSubmit}
							>
								<Text>Let&apos;s go</Text>
								<ChevronRight />
							</Button>
						</div>
					</Form>
				</div>
			</div>
		</div>
	);
};
